import { useCallback, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  SortingState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { ScrollableTable } from './table/scrollable-table';
import { Permission } from '../../../../backend/src/auth/permissions/permission';
import { PermissionGate } from '../../hooks/useHasPermission';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useAddPendingInvite } from '@/api/mutations/useAddPendingInvite';
import { useDeletePendingInvite } from '@/api/mutations/useDeletePendingInvite';
import useGetPendingInvites from '@/api/queries/useGetPendingInvites';
import { User } from '@/api/types/backend-dto-types';
import { useUserContext } from '@/Contexts/User';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/new/components/shadcn/dialog';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/new/components/shadcn/form';
import { Input } from '@/new/components/shadcn/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';

const emailFormSchema = z.object({
  email: z.email({ message: 'Please enter a valid email address' }).toLowerCase(),
});

type EmailForm = z.infer<typeof emailFormSchema>;

interface UserTableProps {
  users: User[];
  addedUsers: User[];
  onUserChange: (users: User[]) => void;
  matterCreatorId?: string;
}

export function UserTable({ users, addedUsers, onUserChange, matterCreatorId }: UserTableProps) {
  const { matterId } = useParams();
  if (!matterId) return;

  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);

  const { user } = useUserContext();

  // Pending invites
  const { data: pendingInvites } = useGetPendingInvites({ matterId });
  const { mutate: addPendingInvite } = useAddPendingInvite({ matterId });
  const { mutate: deletePendingInvite } = useDeletePendingInvite({ matterId });

  const form = useForm<EmailForm>({
    resolver: zodResolver(emailFormSchema),
    defaultValues: {
      email: '',
    },
  });

  const handleInviteUser = form.handleSubmit((data) => {
    trackCustomEvent(TrackingEventsV2.MatterInviteUserNoAccount, {
      userId: user?.id,
      orgId: user?.organisationId,
    });
    addPendingInvite({ userEmail: data.email });
    form.reset();
    setIsInviteDialogOpen(false);
  });

  const handleRemovePendingInvite = (inviteId: string) => {
    deletePendingInvite({ inviteId });
  };

  const handleInviteFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    handleInviteUser(e);
  };

  const handleInviteFormCancel = useCallback(() => {
    form.reset();
    setIsInviteDialogOpen(false);
  }, [form]);

  const handleInviteFormAddUser = () => {
    const existingUser = users.find((u) => u.email.toLowerCase() === form.watch('email').toLowerCase());
    if (existingUser) {
      handleAddUser(existingUser);
      form.reset();
      setIsInviteDialogOpen(false);
    }
  };

  // Separate users into added and available
  const { availableUsers } = useMemo(() => {
    const added: User[] = [];
    const available: User[] = [];

    users.forEach((u) => {
      if (addedUsers.some((au) => au.id === u.id) || u.id === matterCreatorId) {
        added.push(u);
      } else {
        available.push(u);
      }
    });

    return { availableUsers: available };
  }, [users, addedUsers, matterCreatorId]);

  const handleAddUser = (user: User) => {
    trackCustomEvent(TrackingEventsV2.MatterAddTeamMemberOnCreator, { userId: user.id });
    onUserChange([...addedUsers, user]);
  };

  const availableTable = useReactTable({
    data: availableUsers,
    columns: [
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => {
          const isDisabled = row.original.disabled;
          return (
            <div
              className={`text-left lowercase ${isDisabled ? 'text-gray-400' : ''}`}
              data-testid={`available-user-email-${row.original.id}`}
            >
              {row.getValue('email')}
              {isDisabled && <span className="ml-2 text-xs text-gray-500">(disabled)</span>}
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => {
          const isDisabled = row.original.disabled;
          return (
            <div className="flex justify-end">
              <PermissionGate permission={Permission.MatterAddUsers} uiMode="disabled">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleAddUser(row.original)}
                  disabled={isDisabled}
                  data-testid={`add-user-${row.original.id}`}
                >
                  <Icon className="lucide--plus" />
                  Add
                </Button>
              </PermissionGate>
            </div>
          );
        },
      },
    ],
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    getRowId: (row) => row.id,
    globalFilterFn: 'includesString',
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
    state: {
      sorting,
      globalFilter,
    },
  });

  const tableButtons = (
    <>
      {pendingInvites && pendingInvites.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-sm text-amber-700 transition-colors hover:bg-amber-100"
              data-testid="pending-invites-badge"
            >
              Pending Invites ({pendingInvites.length})
              <Icon className="lucide--chevron-down" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-96" align="start">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Pending Invites</h3>
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
                  {pendingInvites.length}
                </span>
              </div>
              <p className="text-xs text-gray-500">
                These users will be added to the matter when they create their account within 7 days.
              </p>
              <div className="max-h-96 overflow-y-auto">
                <div className="space-y-1">
                  {pendingInvites.map((invite) => (
                    <div
                      key={invite.id}
                      className="flex items-center justify-between border-b border-gray-100 bg-white p-2"
                    >
                      <div className="flex flex-col">
                        <span className="text-left text-sm lowercase" data-testid={`pending-invite-email-${invite.id}`}>
                          {invite.userEmail}
                        </span>
                        <span className="text-xs text-gray-400">
                          Invited {new Date(invite.createdDate).toLocaleDateString()}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleRemovePendingInvite(invite.id)}
                        data-testid={`remove-pending-invite-${invite.id}`}
                      >
                        <Icon className="lucide--x" />
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
      <PermissionGate permission={Permission.MatterAddUsers} uiMode="disabled">
        <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="invite-user-button" variant="primary">
              Invite User
              <Icon className="lucide--user-plus" />
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite User to Wexler</DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={handleInviteFormSubmit} className="space-y-4">
                <p className="text-sm text-gray-600">
                  Can't find the user you're looking for? Enter the email address of the person you'd like to invite to
                  join your team on Wexler.
                </p>

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input {...field} placeholder="name@example.com" type="email" autoFocus />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex justify-between gap-2">
                  <Button type="button" variant="outline" onClick={handleInviteFormCancel}>
                    Cancel
                  </Button>

                  {!users.map((u) => u.email.toLowerCase()).includes(form.watch('email').toLowerCase()) ? (
                    <Button type="submit" variant="primary" disabled={!form.watch('email')?.trim()}>
                      Send Invitation
                      <Icon className="lucide--send" />
                    </Button>
                  ) : addedUsers.map((u) => u.email.toLowerCase()).includes(form.watch('email').toLowerCase()) ? (
                    <Button disabled={true}>User already added</Button>
                  ) : (
                    <Button type="button" variant="primary" onClick={handleInviteFormAddUser}>
                      + Add existing user
                    </Button>
                  )}
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </PermissionGate>
    </>
  );

  return (
    <ScrollableTable
      table={availableTable}
      globalFilter={globalFilter}
      setGlobalFilter={setGlobalFilter}
      plural="available users"
      additionalButtons={tableButtons}
      className="-mx-8"
    />
  );
}
